import * as React from 'react'
import { useRouter } from 'next/router'

import { Stack, useTheme } from '@mui/material'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import { styled } from '@mui/material/styles'

import { usePathProgress, useResourceOverview, useLessonProgress } from 'components/hooks/state'
import { I18n, useTranslation } from 'components/hooks/use-translation'
import { PathProgressLoading } from 'pages/path/progress'
import Button from 'components/atomic/atoms/button'
import Text from 'components/atomic/atoms/text'
import Icon from 'components/atomic/atoms/icon'

import { mapIcon, ResourceTypes } from 'utils/resources'
import Routes from 'utils/routes'

const Card = styled(Stack)`
    padding: 1rem;
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
    border: ${({ theme }) => `1px solid ${theme.palette.divider}`};
`

interface ResumeContentProps {
    pathId: string
}

const ResumeContent = React.memo(({ pathId }: ResumeContentProps) => {
    const [timeToComplete, setTimeToComplete] = React.useState<number>()

    const { t }: I18n = useTranslation()
    const { palette } = useTheme()
    const router = useRouter()

    const { data: pathProgress } = usePathProgress({ pathId })

    const { data: resource } = useResourceOverview({
        resourceId: pathProgress?.current_resource,
        shouldFetch: !!pathProgress?.current_resource,
    })

    const { data: lessonProgress } = useLessonProgress({
        lessonId: resource?._id,
        shouldFetch: resource?.type === ResourceTypes.LESSON,
    })

    const buttonCopy = React.useMemo(() => {
        return pathProgress?.resources.map((r) => r._id).includes(pathProgress?.current_resource)
            ? t('path:buttons.continueResource')
            : t('path:buttons.startResource')
    }, [pathProgress, t])

    React.useEffect(() => {
        if (resource) setTimeToComplete(Math.round(resource.time))
    }, [resource])

    const handleClick = () => {
        if (!resource) return

        if (resource?.type === ResourceTypes.LESSON) {
            router.push(Routes.Exercise('paths', pathId, resource?._id, lessonProgress?.current_exercise_number || 1))
        } else {
            router.push(Routes.Resource('paths', pathId, resource?.type, resource?._id))
        }
    }

    if (!pathProgress || !resource) {
        return <PathProgressLoading />
    }

    return (
        <Stack spacing="1rem">
            <Text variant="h4">{t('path:pathProgressSection.startFrom')}</Text>
            <Card direction="row" justifyContent="space-between" alignItems="flex-end">
                <Stack spacing="1rem">
                    <Stack direction="row" spacing="0.5rem" alignItems="center">
                        <Icon color="primary" fontSize="small" name={mapIcon(resource.type)} />
                        <Text variant="overline" color="primary">
                            {t(`resources:types.${resource.type}`)}
                        </Text>
                    </Stack>

                    <Text variant="h5">{resource.title}</Text>

                    <Stack direction="row" spacing="0.5rem">
                        <Icon fontSize="small" name="time" sx={{ color: palette.text.secondary }} />
                        <Text variant="body2" color="text.secondary">
                            {t('path:pathProgressSection.remaining', { value: timeToComplete })}
                        </Text>
                    </Stack>
                </Stack>
                <Button variant="outlined" onClick={() => handleClick()} endIcon={<PlayCircleIcon />}>
                    {buttonCopy}
                </Button>
            </Card>
        </Stack>
    )
})

export default ResumeContent
